import { OrganizationCodes } from '@revenuemarket/bank';

export const DENIAL_MESSAGE = {
  SHORT_RUNWAY: '런웨이 미달',
  NEED_REQUIREMENTS: '최소 요건 미달',
  UC_FAIL: '자체 모형 점수 미달',
  BELOW_MINIMUM_LIMIT: '최소 한도 미달',
  NO_TRADABLE_REVENUE: '거래 가능 채권 없음',
} as const;

export type DenialType = keyof typeof DENIAL_MESSAGE;

export type B2CRevenueType = 'PG' | 'MARKET_PLACE' | 'CARD' | 'APP_STORE';
export type RawB2CRevenueStatus = 'NOT_CONNECTED' | 'ANALYZING' | 'DONE';
export type B2CRevenueStatus =
  | 'BEFORE_CONNECT'
  | 'IN_ANALYSIS'
  | 'CONNECTED_WITH_REVENUE'
  | 'CONNECTED_WITHOUT_REVENUE';

export enum QualifiedStatus {
  QUALIFIED = 'QUALIFIED',
  CHECKING = 'CHECKING',
  NEVER_UPDATED = 'NEVER_UPDATED',
  NOT_UP_TO_DATE = 'NOT_UP_TO_DATE',
  DENIED = 'DENIED',
}

export interface DataQualification {
  revenue: QualifiedStatus;
  bank: QualifiedStatus;
  corpCard: QualifiedStatus;
}

interface TradeBankAccount {
  id: number;
  accountNumber: string;
  bankCode: OrganizationCodes;
}

/**
 TAX_INVOICE: 세금계산서만 있는 경우
 BANK: B2C 매출만 있는 경우
 INVOICE_AND_BANK: 세금계산서 및 B2C 매출 둘 다 있는 경우
 FILE: 파일로 제출한 매출인 경우
 */
export type SourceType = string;

export enum TradeDetailStatus {
  CANCELED,
  DENIED,
  APPLIED,
  APPROVED,
  PAID_OUT,
  REPAID,
  TRADE_DONE,
}

export enum WithdrawalOption {
  BANKINGPLUS = '뱅킹플러스',
  HYOSUNG = '효성 CMS',
  MANUAL = '직접 입금',
}

type AgreeStatus = 'TRANSFERABLE' | 'CMS' | 'CONTRACT';

export interface Assignee {
  /** id */
  id: number;
  /** 이름 */
  name: string;
  /** 별칭 */
  alias: string;
  /** 은행 code */
  bankOrganizationCode: OrganizationCodes;
  /** 은행 별칭 */
  bankAccountAlias: string;
  /** 은행계좌 번호 */
  bankAccountNumber: string;
  /** 잔고 관련 정보 */
  payoutSourceTrend: {
    /** 양수인 id */
    assigneeId: number;
    /** 금액 */
    balance: number;
    /** 기준일 */
    baseDate: string;
  };
}

export interface AssigneeData extends Assignee {
  beforeAssignPayout?: number;
  afterAssignPayout?: number;
  availableForPeriod?: boolean;
}

interface IAssignee {
  /** 양수인 id */
  id: number;
  /** 양수인 이름 */
  name: string;
  /** 별칭 */
  alias: string;
  /** 기관 code */
  bankOrganizationCode: string;
  /** 계좌별칭  */
  bankAccountAlias: string;
  /** 계좌번호 */
  bankAccountNumber: string;
  /** 하이픈 집금 계좌 번호 */
  hpnBankAccountNumber: string;
  /** 하이픈 집금 계좌 별칭 */
  hpnBankAccountAlias: string;
  /** 하이픈 집금 계좌 기관 code */
  hpnBankCode: string;
}

interface IDividendsItem {
  /** 할당 정보 */
  assignee: IAssignee;
  /** 회수 매출 */
  repaymentAmount: number;
  /** 원금 */
  principalAmount: number;
  /** 할인금 */
  discountAmount: number;
  /** 부가세 */
  vatTaxAmount: number;
  /** 회수금 */
  totalAmount: number;
  /** 양수 비율 */
  proportion: number;
}

export interface PayoutAssignment {
  id: number;
  companyId: string;
  tradeId: number;
  assignee: IAssignee;
  amount: number;
  proportion: number;
  createdAt: string;
  updatedAt: string;
}

export interface Distribution {
  tradeId: number;
  repaymentRound: number;
  repaymentDue: string;
  repaymentStatus: string;
  dividends: IDividendsItem[];
}

export interface TradeDetail {
  /** company id */
  companyId: string;
  /** trade id */
  tradeId: number;
  /** 지급액 */
  payout: number;
  /** 상태 */
  status: keyof typeof TradeDetailStatus;
  /** 신청일 */
  appliedDate: string;
  /** 승인일 */
  approvedDate: string;
  /** 지급일 */
  paidOutDate: string;
  /** 상환일 */
  repaidDate: string;
  /** 업데이트 일시 */
  updatedAt: string;
  /** payout batch id */
  payoutBatchId?: number | null;
  /** trade 정보 */
  tradeInfo: TradeInfo;
  /** 약관 */
  agreements: {
    id: number;
    type: AgreeStatus;
    url: string;
  }[];
  /** 성장자금 할당 정보 */
  payoutAssignments: PayoutAssignment[];
  withdrawalBankAccount: {
    bankAccountId: number;
    accountNumber: string;
    bankCode: OrganizationCodes;
  };
  /** 상환 정보 */
  repaymentInfo: {
    /** 총 상환액 */
    totalRepayment: number;
    /** 남은 상환액 */
    remainingRepayment: number;
    /** 상환달 */
    repaymentDayOfMonth?: number;
    /** 월별 상환 금액 */
    repaymentPerMonth?: number;
    /** 첫 상환일 */
    firstRepaymentDate?: string;
    /** 마지막 상환일 */
    lastRepaymentDate?: string;
    /** 상환 계좌 */
    withdrawalAccount?: TradeBankAccount;
    /** 상환 상세 정보 */
    repayments: {
      /** 분배 관련 정보 */
      distribution: Distribution;
      /** 상환 round */
      repaymentRound: number;
      /** 상환 날짜 */
      repaymentDue: string;
      /** 상환 상태 */
      status: string;
      /** 회차별 상환 금액 */
      repaymentPerRound: number;
      /** 회차별 수수료 */
      vatPerRound: number;
      /** 회퐈별 인출액 */
      withdrawalAmountPerRound: number;
      /** 회차별 원금 */
      principalAmountPerRound: number;
      /** 회차별 할인액 */
      discountAmountPerRound: number;
    }[];
  };
}

export type TradeInfo = {
  totalTradedRevenue: number;
  tradingPeriod: number;
  discountRate: number;
  revenueStreams: TradedRevenueStream[];
  assignee: Assignee;
  mainBankAccount: TradeBankAccount;
};

export type TradedRevenueStream = {
  /** 고객사 */
  customerName: string;
  /** 매출 계획(?) */
  planName: string;
  /** 월 양도 매출 */
  tradedRevenuePerPeriod: number;
  /** 총 양도 매출 */
  totalTradedRevenue: number;
  /** 할인율 */
  discountRate: number;
  /** 성장자금 */
  payout: number;
  /** id */
  id: number;
  /** company Id */
  companyId: string;
  /** revenue stream id */
  revenueStreamId: number;
  /** 생성 시각 */
  createdAt: string;
  /** 업데이트 시각 */
  updatedAt: string;
  /** 체크 가능 여부 */
  frozen: boolean;
  assignee: Assignee;
};

export enum UploadRevenueErrorCode {
  DUPLICATE_CUSTOMER_ID = 'DUPLICATE_CUSTOMER_ID',
  INVALID_CUSTOMER_ID = 'INVALID_CUSTOMER_ID',
  BAD_TEMPLATE = 'BAD_TEMPLATE',
}

export const enum AccountStatus {
  Unknown = 'UNKNOWN',
  Normal = 'NORMAL',
  CertificateUnknown = 'CERTIFICATE_UNKNOWN',
  CertificateExpired = 'CERTIFICATE_EXPIRED',
  CertificateDestroyed = 'CERTIFICATE_DESTROYED',
  CertificateDeleted = 'CERTIFICATE_DELETED',
  CertificateNotRegistered = 'CERTIFICATE_NOT_REGISTERED',
  AccountNotExist = 'ACCOUNT_NOT_EXIST',
  AccountRemoved = 'ACCOUNT_REMOVED',
  AccountExpired = 'ACCOUNT_EXPIRED',
  Error = 'ERROR',
  AccountUnscrapable = 'ACCOUNT_UNSCRAPABLE',
  NoPermission = 'NO_PERMISSION',
  AccountSuspended = 'ACCOUNT_SUSPENDED',
}

export const AccountStatusTextMap: Record<AccountStatus, string> = {
  [AccountStatus.Unknown]: '수집 불가',
  [AccountStatus.Normal]: '정상',
  [AccountStatus.CertificateUnknown]: '수집 불가',
  [AccountStatus.CertificateExpired]: '인증서 만료',
  [AccountStatus.CertificateDestroyed]: '인증서 폐기',
  [AccountStatus.CertificateDeleted]: '인증서 삭제',
  [AccountStatus.CertificateNotRegistered]: '기관 미등록',
  [AccountStatus.AccountNotExist]: '비활성 계좌',
  [AccountStatus.AccountRemoved]: '비활성 계좌',
  [AccountStatus.AccountExpired]: '계좌 만료',
  [AccountStatus.Error]: '수집 불가',
  [AccountStatus.AccountUnscrapable]: '수집 실패',
  [AccountStatus.NoPermission]: '조회 권한 없음',
  [AccountStatus.AccountSuspended]: '중지 계좌',
};

export const enum ScrapeStatus {
  Normal = 'NORMAL',
  Error = 'ERROR',
}
